export interface FieldDefinition {
  fieldType: string;
  label: string;
  labelDe: string;
  urlParam: string;
  contactField: string | null;
  placeholderKey: string | null;
  inputType: 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'select' | 'number';
  validation?: 'email' | 'phone' | 'date' | 'number' | 'url';
  icon?: string;
  description?: string;
  descriptionDe?: string;
  options?: string[]; // For select fields: enum values
  optionLabels?: Record<string, string>; // i18n translation keys for options
  // Strukturierte Adressfelder: mappen NICHT auf ContactProfile, sondern auf
  // contact_billing_address / contact_shipping_address (Stripe-kompatibel).
  addressTarget?: 'billing' | 'shipping';
  addressColumn?: 'line1' | 'line2' | 'city' | 'state' | 'postal_code' | 'country';
  group?: string; // Dropdown-/Palette-Gruppierung, z.B. "Rechnungsadresse"
}

// Erzeugt die 6 strukturierten Adressfelder einer Gruppe (billing|shipping).
function addressGroup(target: 'billing' | 'shipping'): Record<string, FieldDefinition> {
  const suffixDe = target === 'billing' ? 'Rechnung' : 'Lieferung';
  const groupDe = target === 'billing' ? 'Rechnungsadresse' : 'Lieferadresse';
  const p = target; // key-prefix: 'billing' | 'shipping'
  const mk = (col: FieldDefinition['addressColumn'], labelDe: string, label: string, icon: string, inputType: FieldDefinition['inputType'] = 'text'): FieldDefinition => ({
    fieldType: `${p}_${col}`,
    label: `${label} (${target})`,
    labelDe: `${labelDe} (${suffixDe})`,
    urlParam: `${p}_${col}`,
    contactField: null,
    placeholderKey: null,
    inputType,
    icon,
    description: `${label} of the ${target} address`,
    descriptionDe: `${labelDe} der ${groupDe}`,
    addressTarget: target,
    addressColumn: col,
    group: groupDe,
  });
  return {
    [`${p}_line1`]: mk('line1', 'Straße & Hausnr.', 'Street', 'MapPin'),
    [`${p}_line2`]: mk('line2', 'Adresszusatz', 'Address line 2', 'MapPin'),
    [`${p}_postal_code`]: mk('postal_code', 'PLZ', 'ZIP / Postal code', 'Hash'),
    [`${p}_city`]: mk('city', 'Stadt', 'City', 'Building'),
    [`${p}_state`]: mk('state', 'Bundesland', 'State', 'Map'),
    [`${p}_country`]: mk('country', 'Land', 'Country', 'Globe'),
  };
}

export const STANDARD_FIELD_DEFINITIONS: Record<string, FieldDefinition> = {
  first_name: {
    fieldType: 'first_name',
    label: 'First Name',
    labelDe: 'Vorname',
    urlParam: 'firstName',
    contactField: 'firstName',
    placeholderKey: 'contact.firstName',
    inputType: 'text',
    icon: 'User',
    description: 'Contact first name',
    descriptionDe: 'Vorname des Kontakts',
  },
  last_name: {
    fieldType: 'last_name',
    label: 'Last Name',
    labelDe: 'Nachname',
    urlParam: 'lastName',
    contactField: 'lastName',
    placeholderKey: 'contact.lastName',
    inputType: 'text',
    icon: 'User',
    description: 'Contact last name',
    descriptionDe: 'Nachname des Kontakts',
  },
  email: {
    fieldType: 'email',
    label: 'Email',
    labelDe: 'E-Mail-Adresse',
    urlParam: 'email',
    contactField: 'email',
    placeholderKey: 'contact.email',
    inputType: 'email',
    validation: 'email',
    icon: 'Mail',
    description: 'Contact email address',
    descriptionDe: 'E-Mail-Adresse des Kontakts',
  },
  phone: {
    fieldType: 'phone',
    label: 'Phone',
    labelDe: 'Handynummer',
    urlParam: 'phone',
    contactField: 'phone',
    placeholderKey: 'contact.phone',
    inputType: 'tel',
    validation: 'phone',
    icon: 'Phone',
    description: 'Contact phone number',
    descriptionDe: 'Telefonnummer des Kontakts',
  },
  phone_country: {
    fieldType: 'phone_country',
    label: 'Phone Country',
    labelDe: 'Ländervorwahl',
    urlParam: 'phoneCountry',
    contactField: 'phoneCountry',
    placeholderKey: 'contact.phoneCountry',
    inputType: 'text',
    icon: 'Flag',
    description: 'Phone country code (e.g. DE, US)',
    descriptionDe: 'Ländercode für Telefonnummer (z.B. DE, US)',
  },
  birthday: {
    fieldType: 'birthday',
    label: 'Birthday',
    labelDe: 'Geburtstag',
    urlParam: 'birthday',
    contactField: 'birthday',
    placeholderKey: 'contact.birthday',
    inputType: 'date',
    validation: 'date',
    icon: 'Cake',
    description: 'Contact birthday',
    descriptionDe: 'Geburtstag des Kontakts',
  },
  gender: {
    fieldType: 'gender',
    label: 'Gender',
    labelDe: 'Geschlecht',
    urlParam: 'gender',
    contactField: 'gender',
    placeholderKey: null,
    inputType: 'select',
    icon: 'Users',
    options: ['male', 'female', 'divers'],
    optionLabels: { male: 'Männlich', female: 'Weiblich', divers: 'Divers' },
    description: 'Contact gender',
    descriptionDe: 'Geschlecht des Kontakts',
  },
  address: {
    fieldType: 'address',
    label: 'Address',
    labelDe: 'Adresse',
    urlParam: 'address',
    contactField: 'address',
    placeholderKey: 'contact.address',
    inputType: 'textarea',
    icon: 'MapPin',
    description: 'Contact address',
    descriptionDe: 'Adresse des Kontakts',
  },
  city: {
    fieldType: 'city',
    label: 'City',
    labelDe: 'Stadt',
    urlParam: 'city',
    contactField: 'city',
    placeholderKey: 'contact.city',
    inputType: 'text',
    icon: 'Building',
    description: 'Contact city',
    descriptionDe: 'Stadt des Kontakts',
  },
  zip_code: {
    fieldType: 'zip_code',
    label: 'ZIP Code',
    labelDe: 'Postleitzahl',
    urlParam: 'zipCode',
    contactField: 'zipCode',
    placeholderKey: 'contact.zipCode',
    inputType: 'text',
    icon: 'Hash',
    description: 'Contact ZIP/postal code',
    descriptionDe: 'Postleitzahl des Kontakts',
  },
  country: {
    fieldType: 'country',
    label: 'Country',
    labelDe: 'Land',
    urlParam: 'country',
    contactField: 'country',
    placeholderKey: 'contact.country',
    inputType: 'text',
    icon: 'Globe',
    description: 'Contact country',
    descriptionDe: 'Land des Kontakts',
  },
  // Strukturierte Rechnungs-/Lieferadresse (→ contact_billing_address /
  // contact_shipping_address). Einzeln wählbar; Builder kann alle 6 auf einmal
  // einfügen. Google-Places-Autocomplete am *_line1-Feld füllt die Geschwister.
  ...addressGroup('billing'),
  ...addressGroup('shipping'),
  notes: {
    fieldType: 'notes',
    label: 'Notes',
    labelDe: 'Notizen',
    urlParam: 'notes',
    contactField: null,
    placeholderKey: null,
    inputType: 'textarea',
    icon: 'FileText',
    description: 'Additional notes or comments',
    descriptionDe: 'Zusätzliche Notizen oder Kommentare',
  },
  company: {
    fieldType: 'company',
    label: 'Company',
    labelDe: 'Firma',
    urlParam: 'company',
    contactField: 'company',
    placeholderKey: 'contact.company',
    inputType: 'text',
    icon: 'Building2',
    description: 'Contact company name',
    descriptionDe: 'Firmenname des Kontakts',
  },
  website: {
    fieldType: 'website',
    label: 'Website',
    labelDe: 'Webseite',
    urlParam: 'website',
    contactField: 'website',
    placeholderKey: 'contact.website',
    inputType: 'text',
    validation: 'url',
    icon: 'Link',
    description: 'Contact website URL',
    descriptionDe: 'Webseite des Kontakts',
  },
  weight_goal_type: {
    fieldType: 'weight_goal_type',
    label: 'Weight Goal',
    labelDe: 'Gewichtsziel',
    urlParam: 'weightGoalType',
    contactField: 'weightGoalType',
    placeholderKey: 'contact.weightGoalType',
    inputType: 'select',
    icon: 'Target',
    description: 'Contact weight goal (reduce, maintain, increase)',
    descriptionDe: 'Gewichtsziel des Kontakts (abnehmen, halten, zunehmen)',
    options: ['reduce', 'maintain', 'increase'],
    optionLabels: {
      reduce: 'fields.weightGoalType.reduce',
      maintain: 'fields.weightGoalType.maintain',
      increase: 'fields.weightGoalType.increase',
    },
  },
};

export function getFieldDefinitionByType(fieldType: string): FieldDefinition | undefined {
  return STANDARD_FIELD_DEFINITIONS[fieldType];
}

export function getFieldDefinitionByUrlParam(urlParam: string): FieldDefinition | undefined {
  return Object.values(STANDARD_FIELD_DEFINITIONS).find(def => def.urlParam === urlParam);
}

export function getFieldDefinitionByContactField(contactField: string): FieldDefinition | undefined {
  return Object.values(STANDARD_FIELD_DEFINITIONS).find(def => def.contactField === contactField);
}

export function getUrlParamForFieldType(fieldType: string): string | undefined {
  return STANDARD_FIELD_DEFINITIONS[fieldType]?.urlParam;
}

export function getContactFieldForFieldType(fieldType: string): string | null | undefined {
  return STANDARD_FIELD_DEFINITIONS[fieldType]?.contactField;
}

export function getAllStandardFieldTypes(): string[] {
  return Object.keys(STANDARD_FIELD_DEFINITIONS);
}

export function isStandardFieldType(fieldType: string): boolean {
  return fieldType in STANDARD_FIELD_DEFINITIONS;
}

export const URL_PARAM_TO_FIELD_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(STANDARD_FIELD_DEFINITIONS).map(([fieldType, def]) => [def.urlParam, fieldType])
);

export const CONTACT_FIELD_TO_FIELD_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(STANDARD_FIELD_DEFINITIONS)
    .filter(([, def]) => def.contactField !== null)
    .map(([fieldType, def]) => [def.contactField as string, fieldType])
);
